import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import CloseIcon from "../components/icons/CloseIcon";
import ProductArtworkPreview from "./ProductArtworkPreview";
import { useModalMotion } from "../hooks/useModalMotion";
import {
  getPackDisplayImage,
  getPackIncludedProducts,
  getPackProductKey,
  getPackUnitTotalFromSelections,
  mapPackProductsForCart,
} from "./packService";
import { parseStockQuantity } from "./stockService";
import { formatPrice, getColorLabel, getSizeLabel } from "./shopUtils";

function getPackStock(pack) {
  return (
    parseStockQuantity(pack?.quantity) ??
    parseStockQuantity(pack?.stock) ??
    parseStockQuantity(pack?.available_quantity) ??
    null
  );
}

function CartGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 25 25" fill="none" aria-hidden="true">
      <path
        d="M21.5 16.5H7.5a1 1 0 0 1 0-2h10.44a2.5 2.5 0 0 0 2.41-1.77L22.5 5.74a1 1 0 0 0-.18-.86 1 1 0 0 0-.82-.38H7.26A2.5 2.5 0 0 0 4.44 2.5H3.5a1 1 0 0 0 0 2h.94a.5.5 0 0 1 .5.5l.06.48 1.73 6.76A3 3 0 0 0 7.5 18.5h.18a3 3 0 1 0 5.64 0h2.36a3 3 0 1 0 5.64 0H21.5a1 1 0 0 0 0-2ZM20.22 6.5l-1.31 5.24a.5.5 0 0 1-.5.26H9.28L7.78 6.5h12.44ZM10.5 20.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm8 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z"
        fill="currentColor"
      />
    </svg>
  );
}

function colorLabel(color) {
  return getColorLabel(color);
}

function buildDefaultSelections(pack) {
  const selections = {};
  getPackIncludedProducts(pack).forEach((entry, index) => {
    const key = getPackProductKey(entry, index);
    const sizes = Array.isArray(entry.product?.product_sizes)
      ? entry.product.product_sizes
      : [];
    const colors = Array.isArray(entry.product?.color) ? entry.product.color : [];
    const size = sizes[0] || null;
    const color = colors[0] || null;
    const sizeLabel = getSizeLabel(size) || entry.size || undefined;
    const colorName = colorLabel(color) || entry.color || undefined;
    selections[key] = {
      productKey: key,
      productId: String(entry.product_id || entry.product?.id || entry.id || ""),
      productSizeId: size?.id,
      size: sizeLabel,
      inches: size?.inches || entry.inches || undefined,
      color: colorName,
      sku_code: size?.sku_code,
      unit_price: Number(size?.price ?? entry.unit_price ?? entry.price ?? 0) || 0,
      picture: entry.picture || entry.product?.picture || undefined,
    };
  });
  return selections;
}

function PackModal({ pack, artworkUrl, cartItems = [], onClose, onConfirm }) {
  const included = useMemo(() => getPackIncludedProducts(pack), [pack]);
  const [qty, setQty] = useState(1);
  const [selections, setSelections] = useState(() => buildDefaultSelections(pack));
  const { requestClose, motionClassName } = useModalMotion(onClose);

  useEffect(() => {
    setQty(1);
    setSelections(buildDefaultSelections(pack));
  }, [pack]);

  if (!pack) return null;

  const unit = getPackUnitTotalFromSelections(pack, selections);
  const name =
    typeof pack.name === "string" ? pack.name : String(pack.name || "Pack");
  const packStock = getPackStock(pack);
  const inCart = (cartItems || []).reduce((sum, item) => {
    if (String(item?.productId || item?.id || "") !== String(pack.id)) return sum;
    if (!(item?.isPack || String(item?.type || "").toLowerCase() === "pack")) {
      return sum;
    }
    return sum + (Number(item.quantity) || 0);
  }, 0);
  const remaining =
    packStock == null ? Number.MAX_SAFE_INTEGER : Math.max(0, packStock - inCart);
  const inStock = remaining > 0;
  const canIncrement = remaining > 0 && qty < remaining;

  const updateSelection = (productKey, patch) => {
    setSelections((prev) => ({
      ...prev,
      [productKey]: { ...prev[productKey], ...patch },
    }));
  };

  return (
    <div
      className={`shopModal${motionClassName}`}
      role="dialog"
      aria-modal="true"
      onClick={requestClose}
    >
      <div className="shopModal__card" onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          className="shopModal__close"
          onClick={requestClose}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
        <div className="shopModal__layout">
          <div className="shopModal__media">
            <ProductArtworkPreview
              productImage={getPackDisplayImage(pack)}
              artworkImage={artworkUrl}
              productName={name}
              skuCode={pack.sku_code}
            />
          </div>
          <div className="shopModal__detail">
            <h2 className="shopModal__title">{name}</h2>
            {pack.description ? (
              <p className="shopModal__label" style={{ marginBottom: 12 }}>
                {pack.description}
              </p>
            ) : null}

            {included.map((entry, index) => {
              const key = getPackProductKey(entry, index);
              const selection = selections[key] || {};
              const sizes = Array.isArray(entry.product?.product_sizes)
                ? entry.product.product_sizes
                : [];
              const colors = Array.isArray(entry.product?.color)
                ? entry.product.color
                : [];
              const itemName = entry.name || entry.product?.name || "Item";
              return (
                <div className="shopModal__section" key={key}>
                  <p className="shopModal__label">
                    {itemName}
                    {entry.quantity > 1 ? ` × ${entry.quantity}` : ""}
                  </p>
                  {sizes.length > 1 ? (
                    <div className="shopModal__options" style={{ marginBottom: 8 }}>
                      {sizes.map((size) => {
                        const label = getSizeLabel(size);
                        const selected =
                          String(selection.productSizeId || "") === String(size.id);
                        return (
                          <button
                            key={size.id || label}
                            type="button"
                            className={`shopModal__chip${selected ? " is-on" : ""}`}
                            onClick={() =>
                              updateSelection(key, {
                                productSizeId: size.id,
                                size: label,
                                inches: size.inches,
                                sku_code: size.sku_code,
                                unit_price: Number(size.price) || 0,
                              })
                            }
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                  {colors.length > 1 ? (
                    <div className="shopModal__options">
                      {colors.map((color) => {
                        const label = colorLabel(color);
                        const selected =
                          String(selection.color || "").toLowerCase() ===
                          label.toLowerCase();
                        return (
                          <button
                            key={label}
                            type="button"
                            className={`shopModal__chip${selected ? " is-on" : ""}`}
                            onClick={() => updateSelection(key, { color: label })}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}

            <div className="shopModal__section">
              <p className="shopModal__label">Quantity</p>
              <div className="shopModal__qty">
                <button
                  type="button"
                  onClick={() => setQty((current) => Math.max(1, current - 1))}
                  aria-label="Decrease"
                  disabled={qty <= 1}
                >
                  −
                </button>
                <span>{qty}</span>
                <button
                  type="button"
                  onClick={() =>
                    setQty((current) => (canIncrement ? current + 1 : current))
                  }
                  aria-label="Increase"
                  disabled={!canIncrement}
                >
                  +
                </button>
              </div>
              {packStock != null ? (
                <p
                  className={`shopModal__stock${
                    inStock ? " shopModal__stock--ok" : " shopModal__stock--sold"
                  }`}
                >
                  {inStock ? `In stock: ${remaining} left` : "Sold out"}
                </p>
              ) : null}
            </div>

            <div className="shopModal__section mt-3">
              <p className="shopModal__label">Price</p>
              <p className="shopModal__price">{formatPrice(unit * qty)}</p>
            </div>

            <div className="shopModal__actions">
              <button
                type="button"
                className="shopModal__cancel"
                onClick={requestClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="shopModal__add"
                disabled={!inStock}
                onClick={() => {
                  if (!inStock) {
                    toast.error("This pack is sold out");
                    return;
                  }
                  if (qty > remaining) {
                    toast.error(`Only ${remaining} in stock`);
                    return;
                  }
                  const products = mapPackProductsForCart(pack, qty, selections);
                  onConfirm({
                    quantity: qty,
                    selections,
                    products,
                    unitPrice: getPackUnitTotalFromSelections(pack, selections),
                  });
                }}
              >
                <CartGlyph />
                {inStock ? "Add to cart" : "Sold out"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PackModal;
